import { FC, useMemo } from "react";
import { Box, Grid, Popover, Stack, Typography, styled } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose } from "@/assets/icons";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TOption } from "@/types/common";
import { EMPTY_STR } from "@/constants/string";
import { DATE_FORMAT } from "@/constants/datetime";
import { dayjs } from "@/utils/dayjs";
import { TCaseEditMatchFilterFormExtended } from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";
import { useCaseEditMatchFilterMasterData } from "@/features/case/components/CaseEdit/use-case-edit-match-filter-master-data";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filterValues: Record<string, string | string[]> | null;
};

/** Display field definitions matching filter form layout (2-column, same order) */
type DisplayField =
  | { type: "single"; key: keyof TCaseEditMatchFilterFormExtended; label: string }
  | { type: "dateRange"; keyFrom: keyof TCaseEditMatchFilterFormExtended; keyTo: keyof TCaseEditMatchFilterFormExtended; label: string }
  | { type: "numberRange"; keyFrom: keyof TCaseEditMatchFilterFormExtended; keyTo: keyof TCaseEditMatchFilterFormExtended; label: string };

const DISPLAY_FIELDS: DisplayField[] = [
  { type: "single", key: "status", label: "ステータス" },
  { type: "single", key: "talentNumber", label: "人材管理番号" },
  { type: "single", key: "interviewDate", label: "面接日" },
  { type: "single", key: "fullName", label: "名前" },
  { type: "single", key: "nickName", label: "呼び名" },
  { type: "single", key: "nationalityId", label: "国籍" },
  { type: "single", key: "gender", label: "性別" },
  { type: "numberRange", keyFrom: "ageFrom", keyTo: "ageTo", label: "年齢" },
  { type: "single", key: "residenceStatusId", label: "在留資格" },
  { type: "dateRange", keyFrom: "periodOfStayFrom", keyTo: "periodOfStayTo", label: "在留期限" },
  { type: "single", key: "languageTestLevelId", label: "日本語能力" },
  { type: "single", key: "personInChargeId", label: "担当者" },
];

const resolveLabel = (value: unknown, options: TOption[]): string => {
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_STR.TEXT;
    const labels = value.map((v) => options.find((opt) => String(opt.value) === String(v))?.label).filter(Boolean) as string[];
    return labels.length > 0 ? labels.join(", ") : EMPTY_STR.TEXT;
  }
  if (value === null || value === undefined || value === "" || value === 0) return EMPTY_STR.TEXT;
  const found = options.find((opt) => String(opt.value) === String(value));
  return found ? found.label : String(value);
};

export const CaseEditMatchAppliedConditionDetailPopover: FC<Props> = ({ anchorEl, onClose, filterValues }) => {
  const open = Boolean(anchorEl);
  const { rawOptions } = useCaseEditMatchFilterMasterData();

  const optionsMap = useMemo(
    (): Partial<Record<keyof TCaseEditMatchFilterFormExtended, TOption[]>> => ({
      status: rawOptions.status,
      nationalityId: rawOptions.nationality,
      gender: rawOptions.gender,
      residenceStatusId: rawOptions.residenceStatus,
      languageTestLevelId: rawOptions.japaneseLevelType,
      personInChargeId: rawOptions.personInCharge,
    }),
    [rawOptions],
  );

  const getDisplayValue = (key: string): string => {
    if (!filterValues) return EMPTY_STR.TEXT;
    const value = filterValues[key];
    const options = optionsMap[key as keyof TCaseEditMatchFilterFormExtended];
    if (options) return resolveLabel(value, options);
    // Format interview date array for display
    if (key === "interviewDate" && Array.isArray(value)) {
      if (value.length === 0) return EMPTY_STR.TEXT;
      return value.map((d) => dayjs(String(d)).format(DATE_FORMAT)).join(", ");
    }
    if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : EMPTY_STR.TEXT;
    if (value === null || value === undefined || value === "") return EMPTY_STR.TEXT;
    return String(value);
  };

  /** Format date range as "start〜end", showing partial if only one side is set */
  const getDateRangeValue = (keyFrom: string, keyTo: string): string => {
    if (!filterValues) return EMPTY_STR.TEXT;
    const from = filterValues[keyFrom] as string;
    const to = filterValues[keyTo] as string;
    if (!from && !to) return EMPTY_STR.TEXT;
    return `${from || ""}〜${to || ""}`;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ "& .MuiPaper-root": { mt: 1, p: 0, borderRadius: "8px", boxShadow: "0px 8px 24px 0px #00000026" } }}
    >
      <PopoverBox>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon sx={{ color: black[80], fontSize: 20 }} />
            <Typography variant="sub16Bold">設定した条件</Typography>
          </Stack>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={20} />
          </IconBox>
        </Stack>

        <Grid container rowSpacing={2} columnSpacing={3}>
          {DISPLAY_FIELDS.map((field) => {
            const fieldKey = field.type === "single" ? field.key : `${field.keyFrom}-${field.keyTo}`;
            const displayValue =
              field.type === "single"
                ? getDisplayValue(field.key)
                : field.type === "dateRange" || field.type === "numberRange"
                  ? getDateRangeValue(field.keyFrom, field.keyTo)
                  : EMPTY_STR.TEXT;
            return (
              <Grid item xs={6} key={fieldKey}>
                <Stack spacing={0.5}>
                  <Typography variant="cap12">{field.label}</Typography>
                  <DividerLine />
                  <Typography variant="body14" sx={{ color: text.primary }}>
                    {displayValue}
                  </Typography>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </PopoverBox>
    </Popover>
  );
};

const PopoverBox = styled(Box)`
  width: 780px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  padding: 24px;
  background-color: ${background.white};
`;

const DividerLine = styled(Box)`
  border-bottom: 1px solid ${divider.middle};
`;
